<template>
  <v-row>
    <v-col cols="12">
      <v-card class="card-container" elevation="0">
        <v-data-table
          :headers="headers"
          :items="pagedItems"
          :item-key="'_id'"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :height="tableHeight"
          fixed-header
          hide-default-footer
          :style="tableVars"
        >
          <template v-slot:[`item.name`]="{ item }">
            {{ formatName(item) }}
          </template>

          <template v-slot:[`item.username`]="{ item }">
            {{ item.username || "-" }}
          </template>

          <template v-slot:[`item.email`]="{ item }">
            {{ item.email || "-" }}
          </template>

          <template v-slot:[`item.phone`]="{ item }">
            {{ item.phone || "-" }}
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:[`item.lastLoginAt`]="{ item }">
            {{ formatDate(item.lastLoginAt) }}
          </template>

          <template v-slot:[`item.role`]="{ item }">
            <v-chip
              small
              :color="getRoleDisplay(item.role).color"
              :text-color="getRoleDisplay(item.role).text"
              class="role-chip"
            >
              <v-icon v-if="getRoleDisplay(item.role).icon" left size="18">
                {{ getRoleDisplay(item.role).icon }}
              </v-icon>
              {{ getRoleDisplay(item.role).label }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-btn
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('edit-role', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#3B82F6">mdi-pencil-outline</v-icon>
                </div>
                <span>แก้ไขสิทธิ์</span>
              </v-btn>

              <v-btn
                v-if="isSuperAdmin"
                color="#1F2A39"
                text
                class="d-flex align-center action-btn ml-2"
                @click="$emit('delete', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#EF4444">
                    mdi-trash-can-outline
                  </v-icon>
                </div>
                <span>ลบ</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-row class="mt-4" v-if="admins.length > 10">
        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-start"
        >
          <span class="count-text mr-2">แสดง</span>

          <v-menu v-model="menu" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div
                class="custom-select"
                v-bind="attrs"
                v-on="on"
                :class="{ 'menu-open': menu }"
              >
                <span class="items-per-page-text">
                  {{ currentItemsPerPageText }}
                </span>
                <img src="@/assets/caret_down.png" class="caret-icon ml-1" />
              </div>
            </template>

            <v-list>
              <v-list-item
                v-for="option in itemsPerPageOptions"
                :key="option.value"
                @click="selectOption(option)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <span class="count-text ml-2">
            จากทั้งหมด {{ admins.length }} รายการ
          </span>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-end pa-0"
        >
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="7"
            prev-icon="ก่อนหน้า"
            next-icon="ถัดไป"
            class="mx-2"
            color="#5C96F8"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "Table",

  props: {
    admins: {
      type: Array,
      required: true,
    },
    userRole: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      page: 1,
      menu: false,
      itemsPerPage: 10,
      itemsPerPageOptions: [
        { text: "10", value: 10 },
        { text: "100", value: 100 },
        { text: "ทั้งหมด", value: -1 },
      ],
    };
  },

  computed: {
    pageCount() {
      if (this.itemsPerPage === -1) return 1;
      return Math.ceil(this.admins.length / this.itemsPerPage);
    },

    pagedItems() {
      if (this.itemsPerPage === -1) return this.admins;
      const start = (this.page - 1) * this.itemsPerPage;
      return this.admins.slice(start, start + this.itemsPerPage);
    },

    tableHeight() {
      return this.pagedItems.length < 7 ? "auto" : "380px";
    },

    currentItemsPerPageText() {
      const opt = this.itemsPerPageOptions.find(
        (x) => x.value === this.itemsPerPage
      );
      return opt ? opt.text : this.itemsPerPage;
    },

    isSuperAdmin() {
      return (
        (this.userRole || "").toString().trim().toLowerCase() === "superadmin"
      );
    },

    actionsColWidth() {
      return this.isSuperAdmin ? 220 : 130;
    },
    roleRight() {
      return this.actionsColWidth;
    },
    tableVars() {
      return {
        "--actions-col": `${this.actionsColWidth}px`,
        "--role-right": `${this.roleRight}px`,
      };
    },

    headers() {
      return [
        {
          text: "ชื่อ-นามสกุล",
          value: "name",
          align: "left",
          class: "header-name",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "ชื่อผู้ใช้",
          value: "username",
          align: "left",
          class: "header-username",
          cellClass: "cell-normal",
          sortable: false,
          width: "180px",
        },
        {
          text: "อีเมล",
          value: "email",
          align: "left",
          class: "header-email",
          cellClass: "cell-normal",
          sortable: false,
          width: "260px",
        },
        {
          text: "เบอร์โทรศัพท์",
          value: "phone",
          align: "left",
          class: "header-phone",
          cellClass: "cell-normal",
          sortable: false,
          width: "180px",
        },
        {
          text: "วันที่สร้างผู้ดูแลระบบ",
          value: "createdAt",
          align: "left",
          class: "header-created",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "วันที่เข้าใช้ล่าสุด",
          value: "lastLoginAt",
          align: "left",
          class: "header-lastlogin",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "สิทธิ์ผู้ดูแลระบบ",
          value: "role",
          align: "left",
          class: "header-role",
          cellClass: "cell-role",
          sortable: false,
          width: "180px",
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "left",
          class: "header-actions",
          cellClass: "cell-actions",
          sortable: false,
          width: `${this.actionsColWidth}px`,
        },
      ];
    },
  },

  watch: {
    page() {
      this.scrollToTopOfTable();
    },
    admins() {
      this.page = 1;
      this.scrollToTopOfTable();
    },
  },

  methods: {
    formatName(item) {
      const parts = [item.firstName, item.lastName]
        .map((v) => (v || "").trim())
        .filter(Boolean);

      return parts.length ? parts.join(" ") : "-";
    },

    formatDate(d) {
      if (!d) return "-";

      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";

      const monthsFullThai = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
      ];

      const day = date.getDate();
      const month = monthsFullThai[date.getMonth()];
      const year = date.getFullYear() + 543;

      return `${day} ${month} ${year}`;
    },

    getRoleDisplay(role) {
      const r = (role || "").toString().trim().toLowerCase();
      if (r === "superadmin") {
        return {
          label: "SuperAdmin",
          color: "#EEF2FF",
          text: "#3730A3",
          icon: "mdi-shield-account-outline",
        };
      }
      if (r === "admin") {
        return {
          label: "Admin",
          color: "#DBEAFE",
          text: "#1D4ED8",
          icon: "mdi-account-tie-outline",
        };
      }
      return { label: "-", color: "#F3F4F6", text: "#6B717F", icon: "" };
    },

    selectOption(option) {
      this.itemsPerPage = option.value;
      this.page = 1;
      this.menu = false;
    },

    scrollToTopOfTable() {
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(".v-data-table__wrapper");
        if (wrapper) {
          wrapper.scrollTo({ top: 0 });
        } else {
          window.scrollTo({ top: 0 });
        }
      });
    },
  },
};
</script>

<style scoped>
.card-container {
  overflow: hidden !important;
  border-radius: 12px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.cell-normal),
::v-deep(.cell-actions) {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #1f2a39 !important;
}
::v-deep(.header-name),
::v-deep(.header-username),
::v-deep(.header-email),
::v-deep(.header-phone),
::v-deep(.header-role),
::v-deep(.header-created),
::v-deep(.header-lastlogin),
::v-deep(.header-actions) {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #6b717f !important;
  background-color: #f9fafc !important;
  box-shadow: none !important;
}
.role-chip {
  border-radius: 999px !important;
  padding: 2px 10px !important;
  min-height: 26px !important;
  font-size: 14px !important;
}
::v-deep(.role-chip .v-chip__content) {
  font-size: 14px !important;
  font-weight: 500 !important;
  display: flex;
  align-items: center;
  gap: 4px;
}
.count-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.items-per-page-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}
.caret-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}
.custom-select.menu-open .caret-icon {
  transform: rotate(180deg);
}
::v-deep(.cell-role) {
  position: sticky;
  right: var(--role-right);
  background: #fff;
  z-index: 2;
  box-shadow: -2px 0 3px -1px #1018280f !important;
}
::v-deep(.header-role) {
  position: sticky;
  right: var(--role-right);
  z-index: 3;
  background: #f9fafc;
  box-shadow: -2px 0 3px -1px #1018280f !important;
}
::v-deep(.cell-actions) {
  position: sticky;
  right: 0;
  background: #fff;
  z-index: 3;
}
::v-deep(.header-actions) {
  position: sticky;
  right: 0;
  background: #f9fafc;
  box-shadow: -2px 0 3px -1px #1018280f !important;
}
::v-deep .v-pagination__item,
::v-deep .v-pagination__navigation {
  font-size: 14px !important;
  font-weight: 400 !important;
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.v-pagination .v-pagination__navigation .v-icon) {
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #6b717f !important;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-style: normal !important;
}
::v-deep(.v-pagination .v-pagination__navigation) {
  width: 65px !important;
  height: 34px !important;
}
.action-btn {
  min-width: 0 !important;
  padding: 0 4px !important;
}
.detail-btn {
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
  width: 24px !important;
  height: 24px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>